import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';

// Who is signed in, in three states rather than two.
//
//   undefined  Firebase has not finished looking yet
//   null       it has looked, and nobody is signed in
//   User       signed in
//
// The distinction is the whole point. Two states collapse "we do not know" into "signed out", and
// the app then shows a login form to somebody who never left -- which is exactly what happened once
// the portal was installed as an app: a PWA has no history, so every cold start begins at start_url
// with an auth instance that has not read storage yet.
//
// It waits on authStateReady() before trusting anything. Subscribing to onAuthStateChanged alone is
// not enough: persistence initialisation is asynchronous, and the observer can fire once with null
// before the stored session has been read. That first null is indistinguishable from a real
// sign-out, and acting on it is why a refresh "fixed" the problem -- by the second load the session
// was already in memory.
export const useAuthUser = () => {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe = () => {};

    auth.authStateReady().then(() => {
      if (cancelled) return;
      // Settled: this value is now the truth, including when it is null.
      setUser(auth.currentUser);
      // Then keep following it, so signing out elsewhere in the app is noticed here too.
      unsubscribe = onAuthStateChanged(auth, setUser);
    });

    return () => { cancelled = true; unsubscribe(); };
  }, []);

  return user;
};
